import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useElementMotionStore } from "@/lib/stores/element-motion-store/element-motion-store";
import { PRESERVE_SELECTION_ATTR } from "@/lib/stores/element-motion-store/element-motion-service";
import { ElementListItem, SortableElementListItem } from "../ElementListItem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getElementsByScene } from "@repo/api/utils/composition";
import { IconUpload } from "@tabler/icons-react";
import { SceneTypeDto } from "@repo/api/overlays";
import { getCompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { cn } from "@/lib/utils";

type Props = {
  sceneType: SceneTypeDto;
};

export default function ElementsList({ sceneType }: Props) {
  const updateOverlaysTabState = useEditorStore(
    (state) => state.service.updateOverlaysTabState,
  );

  const handleElementDrag = useEditorStore(
    (state) => state.service.handleElementDrag,
  );

  const seekToElement = useEditorStore((state) => state.service.seekToElement);

  const updateSelectedElementIds = useElementMotionStore(
    (state) => state.service.updateSelectedElementIds,
  );
  const selectedElementIds = useElementMotionStore(
    (state) => state.selectedElementIds,
  );

  const composition = useEditorStore((state) => state.video?.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);

  const elements = useMemo(
    () => getElementsByScene(composition, sceneType),
    [composition, sceneType],
  );
  const elementIds = useMemo(
    () => elements.map((element) => element.externalId),
    [elements],
  );

  // Anchor for Shift-click range selection.
  const lastClickedIdRef = useRef<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);

  const firstMultiSelectedId = useElementMotionStore((state) => {
    if (state.selectedElementIds.size < 2) return null;
    return elementIds.find((id) => state.selectedElementIds.has(id)) ?? null;
  });

  useEffect(() => {
    if (!firstMultiSelectedId) return;

    listRef.current
      ?.querySelector(`[data-element-id="${CSS.escape(firstMultiSelectedId)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [firstMultiSelectedId]);

  const handleElementClick = useCallback(
    (elementExternalId: string, event: React.MouseEvent) => {
      if (event.shiftKey) {
        // A stale anchor (selection cleared elsewhere) must not seed a range.
        const anchorId =
          selectedElementIds.size > 0 ? lastClickedIdRef.current : null;
        const anchorIndex = anchorId ? elementIds.indexOf(anchorId) : -1;
        const targetIndex = elementIds.indexOf(elementExternalId);

        if (anchorIndex === -1 || targetIndex === -1) {
          lastClickedIdRef.current = elementExternalId;
          updateSelectedElementIds([elementExternalId], "replace");
          return;
        }

        const start = Math.min(anchorIndex, targetIndex);
        const end = Math.max(anchorIndex, targetIndex);
        updateSelectedElementIds(elementIds.slice(start, end + 1), "replace");
        return;
      }

      if (event.metaKey || event.ctrlKey) {
        const wasSelected = selectedElementIds.has(elementExternalId);
        updateSelectedElementIds([elementExternalId], "toggle");

        if (!wasSelected) {
          lastClickedIdRef.current = elementExternalId;
        } else if (lastClickedIdRef.current === elementExternalId) {
          lastClickedIdRef.current = null;
        }
        return;
      }

      lastClickedIdRef.current = elementExternalId;
      updateSelectedElementIds([elementExternalId], "replace");
      updateOverlaysTabState((state) => {
        state.currentElement = {
          externalId: elementExternalId,
          sceneType: sceneType,
        };
      });

      seekToElement(elementExternalId, sceneType);
    },
    [
      updateOverlaysTabState,
      updateSelectedElementIds,
      selectedElementIds,
      seekToElement,
      sceneType,
      elementIds,
    ],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const handleImageDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      setActiveId(active.id as string);
    },
    [setActiveId],
  );

  const handleImageDragEnd = useCallback(
    (event: DragEndEvent) => {
      handleElementDrag(event, sceneType);
      setActiveId(null);
      lastClickedIdRef.current = null;
    },
    [handleElementDrag, setActiveId, sceneType],
  );

  const compositionTimings = useMemo(() => {
    if (!composition) return null;

    return getCompositionTimings(composition, animatedImages);
  }, [composition, animatedImages]);

  if (elements.length === 0 && sceneType === "mainScene") {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center text-sm text-text-secondary">
        <p className="mt-4 w-90 text-center">
          Add elements like logos, property details, or agent contact
          information to your video.
        </p>
      </div>
    );
  }

  if (elements.length === 0 && sceneType !== "mainScene") {
    return (
      <p className="mt-2 px-6 text-sm text-text-secondary">
        Add elements from the <span className="font-semibold">bottom bar</span>{" "}
        to get started.
      </p>
    );
  }

  return (
    <div
      ref={listRef}
      className={cn(
        "flex flex-col gap-1",
        sceneType === "mainScene" ? "px-4.5 py-3" : "px-4",
      )}
      {...{ [PRESERVE_SELECTION_ATTR]: "" }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleImageDragStart}
        onDragEnd={handleImageDragEnd}
      >
        <SortableContext
          items={elements.map((element) => element.externalId)}
          strategy={verticalListSortingStrategy}
        >
          {elements.map((element) => (
            <SortableElementListItem
              key={element.externalId}
              elementExternalId={element.externalId}
              onClick={handleElementClick}
              sceneType={sceneType}
              compositionTimings={compositionTimings}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <ElementListItem
              key={activeId}
              elementExternalId={activeId}
              onClick={() => {}}
              sceneType={sceneType}
              compositionTimings={compositionTimings}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

export function MediaUploadDragHover() {
  return (
    <div className="absolute inset-0 m-2 flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-primary-400 bg-primary-100/95">
      <IconUpload stroke={1.5} size={32} color="url(#icon-gradient)" />
      <div className="h-4"></div>
      <h1 className="text-base font-semibold">Drop file here</h1>
      <p className="text-sm text-text-secondary">to add it to the video</p>
    </div>
  );
}

export function MediaUploadingIndicator() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/95">
      <LoadingSpinner size={20} />
      <div className="w-2"></div>
      <p className="text-sm font-medium">Uploading media...</p>
    </div>
  );
}
